import { omit } from 'lodash-es'
import { omitBy } from 'lodash-es'
import { action, computed, makeObservable, observable } from 'mobx'
import qs from 'qs'

export type Sort<T = string> = [sortBy: T, orderType: 'ASC' | 'DESC']
export type Sorts<T = string> = Sort<T>[]

export type PageParamT = {
  page?: number
  size?: number
  totalPages?: number
  isLast?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ParamDataT<T extends Record<string, any> = Record<string, any>> = {
  sorts?: Sorts
} & PageParamT &
  T

export type ReqParamData<T extends ParamDataT> = {
  params?: Partial<T>
  excludeParams?: Partial<keyof T>[]
}

export class ReqParams<T extends ParamDataT = ParamDataT> {
  params = {
    page: 0,
    size: 30,
    isLast: false,
  } as T
  excludeParams: Set<keyof T>

  constructor(data?: ReqParamData<T>) {
    if (data?.params) this.params = { ...this.params, ...data.params }
    this.excludeParams = new Set(['totalPages', 'isLast', ...(data?.excludeParams ?? [])])

    makeObservable(this, {
      params: observable,
      excludeParams: observable,

      filteredParams: computed,
      toQueryString: computed,
      toRequestParams: computed,
      toSizeRequestParams: computed,
      toSizeQueryString: computed,

      set: action,
      setPage: action,
      setLastPage: action,
      nextPage: action,
      setSorts: action,
      resetPage: action,
      setTotalPages: action,
    })
  }

  get filteredParams() {
    return omit(this.params, [...this.excludeParams.values()])
  }

  get toQueryString() {
    return ReqParams.makeQueryParams(this.filteredParams)
  }

  // toQueryString 과 같은 값을 요청 파라미터 객체로 낸다
  get toRequestParams() {
    return ReqParams.makeRequestParams(this.filteredParams)
  }

  get toSizeQueryString() {
    return ReqParams.makeQueryParams(this.filteredParams, { toSize: true })
  }

  get toSizeRequestParams() {
    return ReqParams.makeRequestParams(this.filteredParams, { toSize: true })
  }

  // toqueryString의 makeQueryParams처럼 value가 ''이거나 null이거나 'ALL'인 경우를 제거한 Params 반환
  get validParams() {
    return omitBy(this.filteredParams, (v) => v === '' || v === 'ALL')
  }

  // copy(overwriteProperties: ParamDataT) {
  //   return new PageParams(Object.assign({}, this.properties, overwriteProperties))
  // }

  set(params: Partial<T>) {
    Object.assign(this.params, params)
  }
  setPage(page: ParamDataT['page']) {
    return this.set({ page } as Partial<T>)
  }

  setLastPage(isLast: ParamDataT['isLast']) {
    return this.set({ isLast } as Partial<T>)
  }

  nextPage() {
    const page = this.params.page ?? 0
    return this.set({ page: page + 1 } as Partial<T>)
  }

  setSorts(sorts: ParamDataT['sorts']) {
    return this.set({ sorts } as Partial<T>)
  }

  resetPage() {
    return this.set({ page: 0 } as Partial<T>)
  }

  setTotalPages(totalPages: ParamDataT['totalPages']) {
    return this.set({ totalPages } as Partial<T>)
  }

  // toSize 는 지금 페이지까지를 한 번에 받도록 page 를 0 으로, size 를 그만큼 키운다
  private static splitSorts(params: ParamDataT, options: { toSize: boolean }) {
    const _params = { ...params }
    if (options.toSize) {
      _params.size = ((_params.page ?? 0) + 1) * (_params.size ?? 0)
      _params.page = 0
    }
    const { sorts, ...rest } = _params
    return { sorts, rest: omitBy(rest, (v) => v === '' || v === 'ALL') }
  }

  static makeQueryParams(params: ParamDataT = {}, options = { toSize: false }) {
    const keys = Object.keys(params) /*?*/
    if (keys.length === 0) return ''

    const { sorts, rest: paramObj } = ReqParams.splitSorts(params, options)

    let queryString = qs.stringify(paramObj, {
      addQueryPrefix: true,
      skipNulls: true,
      arrayFormat: 'comma',
    })

    if (sorts) {
      if (queryString.length > 0) {
        queryString += '&'
      } else {
        queryString += '?'
      }
      queryString += sorts
        .map((sort) => qs.stringify({ sort }, { arrayFormat: 'comma', encode: false }))
        .join('&') /*?*/
    }

    return queryString
  }

  static makeRequestParams(
    params: ParamDataT = {},
    options = { toSize: false },
  ): { page?: number; size?: number; sort?: string[] } & Record<string, unknown> {
    const { sorts, rest } = ReqParams.splitSorts(params, options)
    return {
      ...omitBy(rest, (v) => v === null || v === undefined),
      ...(sorts && { sort: sorts.map((sort) => sort.join(',')) }),
    }
  }
}
